import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchMyReports } from '../api/reports';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import type { ReportSummary } from '../api/types';
import { formatDateTime, formatDuration, formatNumber } from '../utils/format';
import { getActiveServerSlug, serverDisplayName, serverGuildPath, serverReportPath } from '../utils/siteContext';

const REPORTS_PER_PAGE = 50;

function reportDuration(report: ReportSummary) {
  if (!report.startedAt || !report.endedAt) return null;
  const started = new Date(report.startedAt).getTime();
  const ended = new Date(report.endedAt).getTime();
  if (!Number.isFinite(started) || !Number.isFinite(ended) || ended <= started) return null;
  return (ended - started) / 1000;
}

function reportGuildLabel(report: ReportSummary, activeServerSlug: string) {
  if (!report.guild) {
    return <span className="text-body-secondary">Personal Logs</span>;
  }

  return (
    <Link className="link-light text-decoration-none" to={serverGuildPath(activeServerSlug, report.guild.slug ?? String(report.guild.id))}>
      &lt;{report.guild.name}&gt;
    </Link>
  );
}

export function ReportsPage() {
  const activeServerSlug = getActiveServerSlug();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ['reports', 'mine', activeServerSlug],
    queryFn: () => fetchMyReports({ serverSlug: activeServerSlug, limit: 500 }),
    enabled: Boolean(activeServerSlug),
  });

  const reports = query.data ?? [];
  const totalPages = Math.max(1, Math.ceil(reports.length / REPORTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const visibleReports = useMemo(
    () => reports.slice((currentPage - 1) * REPORTS_PER_PAGE, currentPage * REPORTS_PER_PAGE),
    [reports, currentPage]
  );

  return (
    <>
      <section className="page-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">Reports</h1>
          <span className="page-hero__server d-block">{serverDisplayName(activeServerSlug)}</span>
        </div>
      </section>

      <div className="content-wrapper page-main">
        {query.isLoading ? <LoadingState label="Loading reports" /> : null}
        {query.isError ? <ErrorState title="Could not load reports" error={query.error} /> : null}

        {query.data ? (
          <section className="card report-content-card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <h2 className="h5 mb-0">My Reports</h2>
                <div className="small text-body-secondary">
                  {reports.length === 0 ? 'No reports' : `${formatNumber(reports.length)} report${reports.length === 1 ? '' : 's'}`}
                </div>
              </div>

              {reports.length === 0 ? (
                <div className="text-body-secondary py-3">You have not uploaded any reports yet.</div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table report-detail-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Report</th>
                          <th>Guild</th>
                          <th>Date</th>
                          <th className="text-end">Length</th>
                          <th className="text-end">Encounters</th>
                          <th className="text-end">POVs</th>
                          <th className="text-end">Uploads</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleReports.map((report) => {
                          const duration = reportDuration(report);
                          return (
                            <tr key={report.id}>
                              <td>
                                <Link className="link-light text-decoration-none fw-semibold" to={serverReportPath(activeServerSlug, report.id)}>
                                  {report.displayName}
                                </Link>
                              </td>
                              <td>{reportGuildLabel(report, activeServerSlug)}</td>
                              <td>{formatDateTime(report.startedAt ?? report.firstUploadAt)}</td>
                              <td className="text-end tabular-nums">{duration ? formatDuration(duration) : '—'}</td>
                              <td className="text-end tabular-nums">{formatNumber(report.encounterCount)}</td>
                              <td className="text-end tabular-nums">{formatNumber(report.submissionCount)}</td>
                              <td className="text-end tabular-nums">{formatNumber(report.uploadCount)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 ? (
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                      <div className="small text-body-secondary">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="btn-group">
                        <button
                          className="btn btn-outline-light btn-sm"
                          type="button"
                          disabled={currentPage <= 1}
                          onClick={() => setPage((value) => Math.max(1, value - 1))}
                        >
                          Previous
                        </button>
                        <button
                          className="btn btn-outline-light btn-sm"
                          type="button"
                          disabled={currentPage >= totalPages}
                          onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
